import { caseInsensitiveText, normalizedDescription } from "./GraphLookup";
import type { ReleaseDocuments } from "../DocumentUtils";
import { getNamespaceForDefinedIn, getNodeKey, normalizeText } from "./GraphMisc";
import { DecisionGraph, type GraphFieldLookup } from "./DecisionGraph";
import type { GraphNode, RuleGraph } from "./GraphTypes";

const normalizeDescription = <S extends string | undefined>(text: S): S => {
  if (!text) {
    return text;
  }
  return normalizeText(text).toLowerCase() as S;
};

const findClosestFallbackNode = (
  nodes: GraphNode[],
  currentNamespace: string | undefined,
  documents: ReleaseDocuments,
): GraphNode | undefined => {
  let bestNode: GraphNode | undefined;
  let bestIndex = Number.POSITIVE_INFINITY;

  for (const node of nodes) {
    const index = getNamespaceForDefinedIn(node.definedIn, documents)?.indexOf(currentNamespace || "");
    if (index === -1 || !index) {
      continue;
    }
    if (index < bestIndex) {
      bestIndex = index;
      bestNode = node;
    }
  }

  return bestNode ?? nodes[0];
};

/** The nodes matching any of the queries, in graph order. */
const nodesMatching = (graph: DecisionGraph, queries: GraphFieldLookup[]): GraphNode[] =>
  graph.nodesByFields(queries).map((id) => graph.node(id) as GraphNode);

export const findNode = (
  graph: RuleGraph,
  documents: ReleaseDocuments | undefined,
  descriptionOrId: string | undefined,
  currentNamespace?: string,
): GraphNode | undefined => {
  if (!descriptionOrId) {
    return undefined;
  }

  graph = DecisionGraph.from(graph as any);
  const nodeDescription = (node: GraphNode) => normalizeDescription(node.description);
  const nodePublicId = (node: GraphNode) => normalizeDescription(node.publicId);
  descriptionOrId = normalizeDescription(descriptionOrId);

  if (!documents) {
    for (const node of nodesMatching(graph, [
      { field: "id", value: descriptionOrId },
      { field: "description", value: descriptionOrId, normalize: normalizedDescription },
      { field: "publicId", value: descriptionOrId, normalize: normalizedDescription },
    ])) {
      if (
        node.id === descriptionOrId ||
        nodeDescription(node) === descriptionOrId ||
        nodePublicId(node) === descriptionOrId
      ) {
        return node;
      }
    }
    return undefined;
  }
  currentNamespace = getNamespaceForDefinedIn(currentNamespace, documents);

  if (descriptionOrId[0] === "/") {
    // They have included scoping to determine the correct path
    // We assume this is a full path
    const els = descriptionOrId.split("/");
    // get rid of leading empty '' due to starting /
    const _desc = els.pop(); // We assume that they can't have / in the description.
    const namespace = els.join("/");
    for (const node of nodesMatching(graph, [
      { field: "id", value: descriptionOrId },
      { field: "description", value: _desc ?? "", normalize: normalizedDescription },
      { field: "publicId", value: descriptionOrId, normalize: normalizedDescription },
    ])) {
      // we also accept nodes without a "definedIn", so we can transform input nodes into derived nodes
      if (
        (node.id === descriptionOrId ||
          nodeDescription(node) === _desc ||
          nodePublicId(node) === descriptionOrId) &&
        (!node.definedIn || normalizeDescription(getNamespaceForDefinedIn(node.definedIn, documents)) === namespace)
      ) {
        return node;
      }
    }
  }
  // Otherwise we have to find using scoping
  const parts = descriptionOrId.split("/");
  const key = parts.pop();
  const entityPath = parts.join("/") || undefined;
  const nodes: GraphNode[] = [];
  const fallbackNodes: GraphNode[] = [];
  for (const node of nodesMatching(graph, [
    { field: "path", value: descriptionOrId },
    { field: "id", value: key ?? "", normalize: caseInsensitiveText },
    { field: "publicId", value: key ?? "", normalize: normalizedDescription },
    { field: "description", value: key ?? "", normalize: normalizedDescription },
    { field: "id", value: descriptionOrId },
    { field: "description", value: descriptionOrId, normalize: normalizedDescription },
    { field: "publicId", value: descriptionOrId, normalize: normalizedDescription },
  ])) {
    if (!node) {
      continue;
    }
    if (node.path === descriptionOrId) {
      nodes.push(node);
      continue;
    }

    const keyMatches =
      node.id?.toLowerCase() === key || nodePublicId(node) === key || nodeDescription(node) === key;
    if (keyMatches) {
      if (!entityPath) {
        nodes.push(node);
        continue;
      }
      const nodeKey = getNodeKey(node, documents);
      if (entityPath === nodeKey.entityPath) {
        nodes.push(node);
        continue;
      }
    }

    if (
      node.id === descriptionOrId ||
      nodeDescription(node) === descriptionOrId ||
      nodePublicId(node) === descriptionOrId
    ) {
      fallbackNodes.push(node);
    }
  }

  if (!nodes?.length) {
    // but maybe they have / in the description name, naughty, but older documents do it

    // sort by closest to namespace
    if (fallbackNodes.length) {
      const node = findClosestFallbackNode(fallbackNodes, currentNamespace, documents);
      return node;
    }

    return undefined;
  }
  if (nodes.length === 1) {
    // There is only one node - great use that
    const node = nodes[0];
    return node;
  }
  // if we don't know what namespace we are from, just take the first
  if (!currentNamespace) {
    const node = nodes[0];
    return node;
  }
  // There are multiple nodes that match this description. We need to determine which one is the most likely scoped attribute
  // To do that we find the one with the closest namespace/definedIn scope
  const currentPath = currentNamespace.split("/");
  const matches: GraphNode[] = [];
  for (const n of nodes) {
    if (!n.definedIn) {
      continue;
      // This should never get here. If the input node existed it would have been picked up in the find above or if it didn't exist it would not return in the find so the false above would trigger it's creation. SHould catch here just to prevent the below code firing accidentally
    }
    const path = getNamespaceForDefinedIn(n.definedIn, documents)?.split("/") || [];
    // Now compare this with the provided namespace. We are trying to find the node that has the 'closest' path to the current namespace
    let matchCount = 0;
    for (let i = 0; i < path.length; i++) {
      const p = path[i];
      if (p === currentPath[i]) matchCount++;
      else {
        // As soon as we don't match stop counting
        break;
      }
    }
    // We record this in the matches at the index of how much it matches
    // If it doesn't match (eg: 0) then we can't link to this one - it's in the wrong chain. They should have been more specific
    if (matchCount > 0) matches[matchCount] = n;
  }
  if (matches.length === 0) return undefined;
  const node = matches.pop(); // Return the closest match
  if (node) {
  }
  return node;
};

/*export const extractEntityKey = (string: string): EntityKey | undefined => {

};*/
